import {Component, Input, OnInit} from '@angular/core';
import {FormGroup} from "@angular/forms";
import {ValidationDetail} from "../../../shared/models/ValidationDetail";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {NGXLogger} from "ngx-logger";
import {NaoService} from "../../services/nao.service";
import {UtilitiesService} from "../../../core/services/utilities.service";
import { FacadeService } from '../../services/facade.service';
import { OfficerCodeData } from '../../models/officer-code-info';

@Component({
  selector: 'app-internal-details',
  templateUrl: './internal-details.component.html',
  styleUrls: ['./internal-details.component.scss'],
})

export class InternalDetailsComponent implements OnInit {
  @Input() internalDetailsFormGroup!: FormGroup;
  @Input() isMandatory!: boolean;
  public validationDetail: ValidationDetail;
  private _destroySubject$: Subject<void> = new Subject();
  officerCodeData: OfficerCodeData[] = [];
  
  /**
   * Constructor
   * @param logger NGXLogger instance
   * @param customerService Customer Service instance
   */
  constructor(
    private logger: NGXLogger,
    private customerService: NaoService,
    public utils: UtilitiesService,
    private facadeService: FacadeService
  ) {
    this.validationDetail = new ValidationDetail(this.internalDetailsFormGroup, new Map<string, string>());
  }

  /**
   * Init
   */
  ngOnInit(): void {
    this._setupValidationMap();
    this._setupStatusTracker();
    this._setupValidationTrigger();
    this.getOfficerCodesInfo();
  }

  /**
   * Fetch officer codes from the facade service
   * This method retrieves officer codes and updates the officerCodeData array.
   */
  async getOfficerCodesInfo() {
    this.officerCodeData = await this.facadeService.getOfficerCode(3, 1);
  }

  /**
   * Handle officer code selection change
   * @param selectedOfficerCode 
   */
  onOfficerCodeChange(selectedOfficerCode: string): void {
    const branchId = this.officerCodeData.find(officer => officer.officerCode === selectedOfficerCode)?.branchId ?? '';
    this.internalDetailsFormGroup.patchValue({ costCenter: branchId });
  }

  ngOnDestroy(): void {
    this._clearValidations();
    this._destroySubject$.next();
  }

  private _setupValidationMap(): void {
    let labelMap = new Map<string, string>();
    labelMap.set("costCenter", "Cost Center");
    labelMap.set("officerCode", "Officer Code");
    this.validationDetail = new ValidationDetail(this.internalDetailsFormGroup, labelMap);
  }

  private _setupStatusTracker(): void {
    this.internalDetailsFormGroup.statusChanges.pipe(takeUntil(this._destroySubject$)).subscribe(state => {
      const isFormDirty = this.internalDetailsFormGroup.dirty;
      this.logger.debug("Personal Data, Status Change:" + state, " touched:" + isFormDirty);
      this._updateValidations();
    });
  }

  private _updateValidations(): void {
    this.customerService.validationsChange.emit(this.validationDetail);
  }

  private _setupValidationTrigger(): void {
    this.customerService.onTriggerValidations.pipe(takeUntil(this._destroySubject$)).subscribe(() => {
      this.internalDetailsFormGroup.markAllAsTouched();
      this.internalDetailsFormGroup.updateValueAndValidity();
    });
  }

  private _clearValidations(): void {
    this.customerService.validationsChange.emit(
      new ValidationDetail(this.internalDetailsFormGroup, this.validationDetail.labels, true)
    );
  }
}
